import { OpsanioHardwareKind } from "src/auth";

/**
 * Content keys of the NetBox hardware selectors on an opsanio questionnaire.
 * Each fact persists as a comma-separated list of NetBox device IDs; the switch
 * key may instead hold the CUSTOMER_SWITCH_VALUE sentinel.
 */
export const HW_SERVERS_KEY = "hardware.servers";
export const HW_SWITCHES_KEY = "hardware.switches";
export const HW_DAC_KEY = "hardware.dac";
/** Customer-site probes picked from NetBox (not an OpsanioHardwareKind). */
export const HW_PROBES_KEY = "hardware.probes";

/** Manually-entered switch details (used when the customer supplies the switch). */
export const HW_SWITCH_MODEL_KEY = "hardware.switch.switch_row.model";
export const HW_SWITCH_MAC_KEY = "hardware.switch.switch_row.switch_mac";
export const HW_SWITCH_SERIAL_KEY = "hardware.switch.switch_row.switch_sn";

/**
 * Parse a hardware content fact into a de-duplicated list of NetBox device IDs.
 * Tolerates the {value} wrapper, raw strings and arrays; non-numeric entries
 * (e.g. the "customer-switch" sentinel) are dropped.
 */
export const parseDeviceIds = (raw: unknown): string[] => {
  let value: unknown = raw;
  if (
    raw &&
    typeof raw === "object" &&
    "value" in (raw as Record<string, unknown>)
  ) {
    value = (raw as Record<string, unknown>).value;
  }
  let list: string[] = [];
  if (Array.isArray(value)) list = value.map((v) => `${v}`);
  else if (typeof value === "string") list = value.split(",");
  return Array.from(
    new Set(list.map((id) => id.trim()).filter((id) => /^\d+$/.test(id))),
  );
};

/** Read a plain string fact off the questionnaire content. */
export const factText = (content: any, key: string): string => {
  const raw = content?.[key]?.value ?? content?.[key];
  if (typeof raw === "string") return raw.trim();
  if (typeof raw === "number") return `${raw}`;
  return "";
};

/** OUI icon per hardware kind. */
export const HARDWARE_KIND_ICON: Record<OpsanioHardwareKind, string> = {
  server: "compute",
  switch: "cluster",
  dac: "link",
};

/** Format a GiB/GB figure for display, promoting to TB above 1024. */
export const formatCapacity = (gb: number | null): string => {
  if (gb === null || !Number.isFinite(gb)) return "-";
  if (gb >= 1024) return `${(gb / 1024).toFixed(1)} TB`;
  return `${gb} GB`;
};
